import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { AddressPill } from '@react/components/addresses/AddressPill'
import ViewNumber from '@react/components/elements/ViewNumber'
import { erc20WeiToCommaDecString } from '@shared/conversions'
import { yupErc20WeiRequired } from '@shared/Enums'
import { EthAddr, StringTypeEnum, toFieldMetaData } from '@shared/SchemaTypes'
import React, { ReactElement } from 'react'
import { SchemaFieldDescription } from 'yup'

import { logOT } from '@shared/loggingShared'
import { LiquidityAssetClass } from './LiquidityAssetClass'
import { ReserveActionPlanClass } from './ReserveActionPlanClass'

function _LiquidityAssetRow({ asset }: { asset: LiquidityAssetClass }): ReactElement {
  logOT.info('LiquidityAssetRow', asset)
  return (
    <TableRow>
      <TableCell sx={{ paddingX: 2, verticalAlign: 'middle' }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {asset.symbol}
        </Typography>
      </TableCell>
      <TableCell sx={{ paddingX: 2, verticalAlign: 'middle' }}>
        <Typography variant="body2">{asset.chain}</Typography>
      </TableCell>
      <TableCell sx={{ paddingX: 2, verticalAlign: 'middle' }}>
        <AddressPill addr={asset.address as EthAddr} />
      </TableCell>
      <TableCell sx={{ paddingX: 2, verticalAlign: 'middle', textAlign: 'right' }}>
        <Typography variant="body2">{erc20WeiToCommaDecString(asset.lytIssuerVaultBalance)}</Typography>
      </TableCell>
      <TableCell sx={{ paddingX: 2, verticalAlign: 'middle', textAlign: 'right' }}>
        <Typography variant="body2">{erc20WeiToCommaDecString(asset.reserveTransferBalance)}</Typography>
      </TableCell>
      <TableCell sx={{ paddingX: 2, verticalAlign: 'middle' }}>
        <AddressPill addr={asset.depositAddress as EthAddr} />
      </TableCell>
      <TableCell sx={{ paddingX: 2, verticalAlign: 'middle' }}>
        <AddressPill addr={asset.reserveTransferAddress as EthAddr} />
      </TableCell>
    </TableRow>
  )
}

function NativeLiquidityAsset({
  asset,
  reserveActionPlanClass,
}: {
  asset: LiquidityAssetClass
  reserveActionPlanClass: ReserveActionPlanClass
}): ReactElement {
  const schema = yupErc20WeiRequired
    .label('Shares')
    .default('0')
    .meta(
      toFieldMetaData({
        propName: 'shares',

        description: 'the shares for the action',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    )
    .describe() as SchemaFieldDescription
  return (
    <Box sx={{ marginBottom: 3 }}>
      <Typography variant="h3" sx={{ marginBottom: 2 }}>
        Native Liquidity Asset
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 2,
          padding: 2,
          backgroundColor: 'grey.50',
          borderRadius: 1,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {asset.symbol}
        </Typography>
        <Typography variant="body2">{asset.chain}</Typography>
        <AddressPill addr={asset.address as EthAddr} />
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr 1fr',
          gap: 2,
          marginBottom: 2,
        }}
      >
        <ViewNumber
          card
          toolTip="Sierra Pool"
          label="Sierra Pool"
          inTable={false}
          liquidityTokenSymbol={asset.symbol}
          poolTokenSymbol=""
          dataValue={asset.lytPoolBalance.toString()}
          fieldSchema={schema}
          value={asset.lytPoolBalance.toString()}
        />
        <ViewNumber
          card
          toolTip="Sierra Vault Balance"
          label="Sierra Vault Balance"
          inTable={false}
          liquidityTokenSymbol={asset.symbol}
          poolTokenSymbol=""
          dataValue={asset.lytIssuerVaultBalance.toString()}
          fieldSchema={schema}
          value={asset.lytIssuerVaultBalance.toString()}
        />

        <ViewNumber
          card
          toolTip="MM Outstanding Redemption Requests"
          label="MM Outstanding Redemption Requests"
          inTable={false}
          liquidityTokenSymbol={asset.symbol}
          poolTokenSymbol=""
          dataValue={reserveActionPlanClass.marketMakerRedeemOutstandingBalance.toString()}
          fieldSchema={schema}
          value={reserveActionPlanClass.marketMakerRedeemOutstandingBalance.toString()}
        />
        <ViewNumber
          card
          toolTip="OT Outstanding Withdrawal Requests"
          label="OT Outstanding Withdrawal Requests"
          inTable={false}
          liquidityTokenSymbol={asset.symbol}
          poolTokenSymbol=""
          dataValue={reserveActionPlanClass.reservesPreWithdrawRequestedOrAcceptedAmount.toString()}
          fieldSchema={schema}
          value={reserveActionPlanClass.reservesPreWithdrawRequestedOrAcceptedAmount.toString()}
        />
      </Box>
    </Box>
  )
}

// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.
export default function LytIssuerLiquidityAssets(props: {
  reserveActionPlan?: ReserveActionPlanClass
  isLoading: boolean
}): React.ReactElement {
  if (props.isLoading || !props.reserveActionPlan) {
    return <Box>TODO LytIssuerLiquidityAssets...</Box>
  }

  const reserveManagementData = props.reserveActionPlan
  return (
    <Paper sx={{ padding: 3, border: '1px solid', borderColor: 'grey.200' }}>
      <NativeLiquidityAsset
        key={'native'}
        asset={reserveManagementData.nativeLiquidityAsset}
        reserveActionPlanClass={reserveManagementData}
      />

      {/* <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 2,
          marginTop: 3,
        }}
      >
        <Typography variant="h3">Liquidity Assets</Typography>
        {roleEnum === RoleEnum.PROTOCOL_MANAGER && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              navigate('/liquidityAssets')
            }}
          >
            Edit
          </Button>
        )}
      </Box> 
      
      <Box sx={{ overflowX: 'auto', width: '100%' }}>
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ paddingX: 2, backgroundColor: 'grey.50' }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Symbol
                </Typography>
              </TableCell>
              <TableCell sx={{ paddingX: 2, backgroundColor: 'grey.50' }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Chain
                </Typography>
              </TableCell>
              <TableCell sx={{ paddingX: 2, backgroundColor: 'grey.50' }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Address
                </Typography>
              </TableCell>
              <TableCell sx={{ paddingX: 2, backgroundColor: 'grey.50', textAlign: 'right' }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Issuer Vault Balance
                </Typography>
              </TableCell>
              <TableCell sx={{ paddingX: 2, backgroundColor: 'grey.50', textAlign: 'right' }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Reserve Transfer Balance
                </Typography>
              </TableCell>
              <TableCell sx={{ paddingX: 2, backgroundColor: 'grey.50' }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Deposit Address
                </Typography>
              </TableCell>
              <TableCell sx={{ paddingX: 2, backgroundColor: 'grey.50' }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Reserve Transfer Address
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reserveManagementData.liquidityAssets
              .filter((asset) => !asset.isLytNative)
              .map((asset, index) => (
                <LiquidityAssetRow key={index} asset={asset} />
              ))}
          </TableBody>
        </Table>
      </Box> */}
    </Paper>
  )
}
